// @flow
import type { ExperienceMap } from 'app/common/lib/react/experiences/experienceUtils';

export type ExperienceFetchAll = {
  type: 'EXPERIENCE_FETCH_ALL',
  payload: {
    isFetchingAll: boolean,
  },
};
// fetches all experiences; this will only occur if no current refresh is ongoing
export function experienceFetchAll(): ExperienceFetchAll {
  return {
    type: 'EXPERIENCE_FETCH_ALL',
    payload: {
      isFetchingAll: true,
    },
  };
}

export type ExperienceFetchAllComplete = {
  type: 'EXPERIENCE_FETCH_ALL_COMPLETE',
  payload: {
    eligibleExperiences: ExperienceMap,
    isFetchingAll: boolean,
  },
};
// experience refresh is completed
export function experienceFetchAllComplete(
  eligibleExperiences: ExperienceMap,
): ExperienceFetchAllComplete {
  return {
    type: 'EXPERIENCE_FETCH_ALL_COMPLETE',
    payload: {
      eligibleExperiences,
      isFetchingAll: false,
    },
  };
}

export type ExperienceFetch = {
  type: 'EXPERIENCE_FETCH',
  payload: {
    placementId: number,
    extraContext: Object,
  },
};
// fetch a single experience with extra context; this should only occur if
// 1) no current experiences are being fetched for this placement
// 2) no renderable experience is already eligible for this placement
// 3) the extra context actually exists (otherwise the experience for this placement would have returned on a refresh)
export function experienceFetch(placementId: number, extraContext?: Object = {}): ExperienceFetch {
  return {
    type: 'EXPERIENCE_FETCH',
    payload: {
      extraContext,
      placementId,
    },
  };
}

export type ExperienceFetchComplete = {
  type: 'EXPERIENCE_FETCH_COMPLETE',
  payload: {
    eligibleExperiences: ExperienceMap,
    extraContext: Object,
    placementId: number,
  },
};
// fetching of a single experience is completed
export function experienceFetchComplete(
  eligibleExperiences: ExperienceMap,
  placementId: number,
  extraContext?: Object = {},
): ExperienceFetchComplete {
  return {
    type: 'EXPERIENCE_FETCH_COMPLETE',
    payload: {
      eligibleExperiences,
      extraContext,
      placementId,
    },
  };
}

export type ExperienceTrigger = {
  type: 'PLACEMENT_EXPERIMENTS_TRIGGER',
  payload: {
    placementId: number,
    extraContext?: Object,
  },
};
// triggers the experiments related to the placementId
export function placementExperimentsTrigger(
  placementId: number,
  extraContext?: Object,
): ExperienceTrigger {
  return {
    type: 'PLACEMENT_EXPERIMENTS_TRIGGER',
    payload: {
      placementId,
      extraContext,
    },
  };
}

export type ExperienceViewed = {
  type: 'EXPERIENCE_VIEWED',
  payload: {
    placementId: number,
    experienceId: number,
    isBackendExperience: boolean,
  },
};
// user viewed an experience
export function experienceView(
  placementId: number,
  experienceId: number,
  isBackendExperience?: boolean = false,
): ExperienceViewed {
  return {
    type: 'EXPERIENCE_VIEWED',
    payload: {
      placementId,
      experienceId,
      isBackendExperience,
    },
  };
}

export type ExperienceCompleted = {
  type: 'EXPERIENCE_COMPLETED',
  payload: {
    placementId: number,
    experienceId: number,
    preventRemoval: boolean,
    isBackendExperience: boolean,
    extraContext: Object,
  },
};
// user completed an experience, it will be removed from the eligible list optimistically unless
// preventRemoval is set
export function experienceComplete(
  placementId: number,
  experienceId: number,
  preventRemoval?: boolean = false,
  isBackendExperience?: boolean = false,
  extraContext?: Object = {},
): ExperienceCompleted {
  return {
    type: 'EXPERIENCE_COMPLETED',
    payload: {
      placementId,
      experienceId,
      preventRemoval,
      isBackendExperience,
      extraContext,
    },
  };
}

export type ExperienceDismissed = {
  type: 'EXPERIENCE_DISMISSED',
  payload: {
    placementId: number,
    experienceId: number,
    preventRemoval: boolean,
    isBackendExperience?: boolean,
  },
};
// user dismissed an experience, it will be removed from the eligible list optimistically unless
// preventRemoval is set
export function experienceDismiss(
  placementId: number,
  experienceId: number,
  preventRemoval?: boolean = false,
  isBackendExperience?: boolean = false,
): ExperienceDismissed {
  return {
    type: 'EXPERIENCE_DISMISSED',
    payload: {
      placementId,
      experienceId,
      preventRemoval,
      isBackendExperience,
    },
  };
}

export type ExperienceRemove = {
  type: 'EXPERIENCE_REMOVE',
  payload: {
    placementId: number,
    experienceId: number,
  },
};
// optimistically remove the experience from the store if it is completed/viewed
export function experienceRemoval(placementId: number, experienceId: number): ExperienceRemove {
  return {
    type: 'EXPERIENCE_REMOVE',
    payload: {
      placementId,
      experienceId,
    },
  };
}

export type PlacementMount = {
  type: 'PLACEMENT_MOUNT',
  payload: {
    placementId: number,
    extraContext: Object,
    isServerSideRender: boolean,
  },
};
// sets this placement as "mounted", which means experiences for this placement can be shown
// it will attempt to fetch a new experience for the placement if no renderable experience exists
// the triggerable_placed_exps will also be triggered
// used by withPlacement HOC
export function placementMount(
  placementId: number,
  extraContext?: Object = {},
  isServerSideRender?: boolean = false,
): PlacementMount {
  return {
    type: 'PLACEMENT_MOUNT',
    payload: {
      placementId,
      extraContext,
      isServerSideRender,
    },
  };
}

export type PlacementUnmount = {
  type: 'PLACEMENT_UNMOUNT',
  payload: {
    placementId: number,
  },
};
// this placement is no longer "mounted"; experiences for this placement should not show anymore
// used by withPlacement HOC
export function placementUnmount(placementId: number): PlacementUnmount {
  return {
    type: 'PLACEMENT_UNMOUNT',
    payload: {
      placementId,
    },
  };
}
